import React, { useCallback } from 'react';
import { Card, Typography } from 'antd';
import { useTheme } from '@/components/common/ThemeProvider';
import { CoaTreeView } from '../components/CoaTreeView';
import { CoaFormDialog } from '../components/CoaFormDialog';
import { useCoa } from '../hooks/use-coa';
import { CoaNode } from '../types';
import { useMessage } from '@/shared/hooks/useMessage';
import { CoaDTO } from "@/shared/api/model";

const { Title } = Typography;

export const CoaManagement: React.FC = () => {
    const { dark } = useTheme();
    const message = useMessage();
    const {
        treeData,
        isLoading,
        activeNode,
        actionMode,
        isModalOpen,
        expandedKeys,
        setModalOpen,
        setActiveNode,
        setActionMode,
        toggleExpanded,
        createCoa,
        updateCoa,
        deleteCoa,
        refetch
    } = useCoa();

    // Helper functions for COA operations
    const getRange = (glNo: string) => {
        const original = parseInt(glNo);
        const root = parseInt(glNo.replace(/0+$/, ""));
        const tens = original / root;
        const next = (root + 1) * tens;

        return {
            start: original,
            end: next,
        };
    };

    const getNextFromRoot = useCallback((code: string): number => {
        const original = parseInt(code);
        const root = parseInt(code.replace(/0+$/, ""));
        const tens = original / root;
        const next = (root * 10 + 1) * (tens / 10);
        return next;
    }, []);

    const getNextFromChild = useCallback((code: string): number => {
        const original = parseInt(code);
        const root = parseInt(code.replace(/0+$/, ""));
        const tens = original / root;
        const next = (root + 1) * tens;
        return next;
    }, []);

    const getSuggestChild = useCallback((parent: string, fsKey: string, currentNodes: CoaNode[]): string => {
        const range = getRange(parent);
        let maxChildGlNo: string | null = null;

        currentNodes.forEach(data => {
            if (
                parseInt(data.glNo) > range.start &&
                parseInt(data.glNo) < range.end &&
                data.parent === fsKey
            ) {
                maxChildGlNo = data.glNo;
            }
        });

        console.log({ maxChildGlNo });

        if (maxChildGlNo === null) {
            return getNextFromRoot(parent).toString();
        }

        return getNextFromChild(maxChildGlNo).toString();
    }, [getNextFromRoot, getNextFromChild]);

    const handleNodeAction = useCallback(async (node: CoaNode, mode: 'view' | 'add' | 'edit' | 'delete') => {
        let nodeToHandle = node;

        if (mode === 'add') {
            nodeToHandle = {
                ...node,
                parent: node.fsKey,
                glNo: getSuggestChild(node.glNo, node.fsKey, treeData),
                acName: `${node.acName} - ${node.glNo}`,
                level: node.level + 1,
                child: 0,
                noOfAccounts: 0,
            };
            if (node.fsKey) {
                toggleExpanded(node.fsKey);
            }
        }

        setActiveNode(nodeToHandle);
        setActionMode(mode);
        setModalOpen(true);
    }, [setActiveNode, setActionMode, setModalOpen, getSuggestChild, treeData, toggleExpanded]);

    const handleFormSubmit = useCallback(async (formData: Partial<CoaDTO>) => {
        try {
            if (actionMode === 'add') {
                await createCoa(formData as CoaDTO);
                message.success('Chart of Account created successfully');
            } else if (actionMode === 'edit') {
                await updateCoa(formData as CoaDTO);
                message.success('Chart of Account updated successfully');
            } else if (actionMode === 'delete' && activeNode) {
                await deleteCoa(activeNode.glNo);
                message.success('Chart of Account deleted successfully');
            }

            await refetch();
            setModalOpen(false);
            setActiveNode(null);

        } catch (error) {
            message.error('Operation failed. Please try again.');
            console.error('Operation failed:', error);
        }
    }, [actionMode, createCoa, updateCoa, deleteCoa, activeNode, refetch, message]);

    const handleCloseModal = useCallback(() => {
        setModalOpen(false);
        setActiveNode(null);
        setActionMode('view');
    }, [setModalOpen, setActiveNode, setActionMode]);

    if (isLoading) {
        return (
            <Card loading className={dark ? 'bg-gray-800' : ''}>
                <div className="h-[calc(100vh-200px)]" />
            </Card>
        );
    }

    return (
        <div className="space-y-4">
            <Title level={2} className={dark ? 'text-white' : ''}>
                Chart of Accounts
            </Title>

            <Card className={dark ? 'bg-gray-800' : ''}>
                <CoaTreeView
                    data={treeData}
                    onNodeAction={handleNodeAction}
                    expandedKeys={expandedKeys}
                    onExpand={toggleExpanded}
                />

                <CoaFormDialog
                    open={isModalOpen}
                    mode={actionMode}
                    //@ts-ignore
                    initialData={activeNode}
                    onCancel={handleCloseModal}
                    onSubmit={handleFormSubmit}
                />
            </Card>
        </div>
    );
};